import React, { useEffect, useState, Fragment } from 'react';
import { TruckIcon, PlusCircleIcon, MinusCircleIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon } from 'lucide-react';
interface TruckingCompany {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  rating: number;
  truckCount: number;
  isDefault?: boolean;
  rates: {
    [key: string]: number;
  };
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  material: string;
  numberOfTrucks: string;
  loadLocation: string;
  dumpLocation: string;
  truckType?: string;
  customer?: string;
  source?: 'broker' | 'contractor';
  contractorName?: string;
  assignments?: {
    truckingCompanyId: string;
    truckCount: number;
  }[];
}
interface AssignJobsToTruckingCompaniesProps {
  jobs: Job[];
  truckingCompanies: TruckingCompany[];
  onAssignJobs: (jobId: string, assignments: {
    truckingCompanyId: string;
    truckCount: number;
  }[]) => void;
}
export const AssignJobsToTruckingCompanies = ({
  jobs,
  truckingCompanies,
  onAssignJobs
}: AssignJobsToTruckingCompaniesProps) => {
  const [assignments, setAssignments] = useState<Record<string, {
    truckingCompanyId: string;
    truckCount: number;
  }[]>>({});
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  // Initialize assignments with default trucking company if not already set
  useEffect(() => {
    const newAssignments = {
      ...assignments
    };
    let updated = false;
    jobs.forEach(job => {
      if (!newAssignments[job.id] && !job.assignments) {
        const defaultCompany = truckingCompanies.find(company => company.isDefault);
        if (defaultCompany) {
          const truckCount = parseInt(job.numberOfTrucks.split(' ')[0]) || 1;
          newAssignments[job.id] = [{
            truckingCompanyId: defaultCompany.id,
            truckCount
          }];
          updated = true;
        }
      } else if (job.assignments && !newAssignments[job.id]) {
        newAssignments[job.id] = job.assignments;
        updated = true;
      }
    });
    if (updated) {
      setAssignments(newAssignments);
    }
  }, [jobs, truckingCompanies]);
  const handleAddTruckingCompany = (jobId: string) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      const assignedCompanyIds = currentAssignments.map(a => a.truckingCompanyId);
      const availableCompany = truckingCompanies.find(c => !assignedCompanyIds.includes(c.id));
      if (availableCompany) {
        return {
          ...prev,
          [jobId]: [...currentAssignments, {
            truckingCompanyId: availableCompany.id,
            truckCount: 1
          }]
        };
      }
      return prev;
    });
  };
  const handleRemoveTruckingCompany = (jobId: string, index: number) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments.splice(index, 1);
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const handleChangeCompany = (jobId: string, index: number, companyId: string) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments[index] = {
        ...currentAssignments[index],
        truckingCompanyId: companyId
      };
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const handleChangeTruckCount = (jobId: string, index: number, truckCount: number) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments[index] = {
        ...currentAssignments[index],
        truckCount
      };
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const getTotalAssignedTrucks = (jobId: string) => {
    return (assignments[jobId] || []).reduce((sum, assignment) => sum + assignment.truckCount, 0);
  };
  const getRequiredTrucks = (job: Job) => {
    return parseInt(job.numberOfTrucks.split(' ')[0]) || 0;
  };
  const isJobFullyAssigned = (job: Job) => {
    return getTotalAssignedTrucks(job.id) === getRequiredTrucks(job);
  };
  const handleAssignJob = (jobId: string) => {
    onAssignJobs(jobId, assignments[jobId] || []);
  };
  return <div className="bg-white rounded-md shadow p-6" data-id="element-453">
      <h2 className="text-xl font-bold mb-4" data-id="element-454">
        Assign Jobs to Trucking Companies
      </h2>
      {jobs.length === 0 ? <div className="bg-gray-50 p-6 rounded-md text-center" data-id="element-455">
          <TruckIcon size={40} className="mx-auto mb-2 text-gray-400" data-id="element-456" />
          <p className="text-gray-600" data-id="element-457">No jobs available for assignment</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-458">
            Accept contractor requests or create new jobs to get started
          </p>
        </div> : <div className="overflow-x-auto" data-id="element-459">
          <table className="w-full border-collapse" data-id="element-460">
            <thead data-id="element-461">
              <tr className="bg-gray-50" data-id="element-462">
                <th className="text-left p-3 border-b" data-id="element-463">Customer</th>
                <th className="text-left p-3 border-b" data-id="element-464">Contractor</th>
                <th className="text-left p-3 border-b" data-id="element-465">Job Name</th>
                <th className="text-left p-3 border-b" data-id="element-466">Job #</th>
                <th className="text-left p-3 border-b" data-id="element-467">Date</th>
                <th className="text-left p-3 border-b" data-id="element-468">Material</th>
                <th className="text-left p-3 border-b" data-id="element-469">Truck Type</th>
                <th className="text-left p-3 border-b" data-id="element-470">Trucks Needed</th>
                <th className="text-left p-3 border-b" data-id="element-471">Location</th>
                <th className="text-left p-3 border-b" data-id="element-472">Assignment Status</th>
                <th className="text-left p-3 border-b" data-id="element-473">Actions</th>
              </tr>
            </thead>
            <tbody data-id="element-474">
              {jobs.map(job => <Fragment key={job.id} data-id="element-475">
                  <tr className="hover:bg-gray-50" data-id="element-476">
                    <td className="p-3 border-b" data-id="element-477">
                      <div className="font-medium text-purple-600" data-id="element-478">
                        {job.customer || 'N/A'}
                      </div>
                    </td>
                    <td className="p-3 border-b" data-id="element-479">
                      {job.source === 'contractor' && job.contractorName ? <div data-id="element-480">
                          <div className="font-medium text-blue-600" data-id="element-481">
                            {job.contractorName}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-482">External</div>
                        </div> : <div data-id="element-483">
                          <div className="font-medium text-green-600" data-id="element-484">
                            {job.contractorName || 'Self'}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-485">Internal</div>
                        </div>}
                    </td>
                    <td className="p-3 border-b" data-id="element-486">
                      <div className="font-medium" data-id="element-487">{job.jobName}</div>
                    </td>
                    <td className="p-3 border-b" data-id="element-488">{job.jobNumber}</td>
                    <td className="p-3 border-b" data-id="element-489">{job.date}</td>
                    <td className="p-3 border-b" data-id="element-490">{job.material}</td>
                    <td className="p-3 border-b" data-id="element-491">
                      <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full" data-id="element-492">
                        {job.truckType || 'Not specified'}
                      </span>
                    </td>
                    <td className="p-3 border-b" data-id="element-493">{job.numberOfTrucks}</td>
                    <td className="p-3 border-b" data-id="element-494">
                      {job.loadLocation} → {job.dumpLocation}
                    </td>
                    <td className="p-3 border-b" data-id="element-495">
                      {isJobFullyAssigned(job) ? <span className="flex items-center text-green-600" data-id="element-496">
                          <CheckCircleIcon size={16} className="mr-1" data-id="element-497" />
                          Fully Assigned
                        </span> : <span className="flex items-center text-orange-500" data-id="element-498">
                          <AlertCircleIcon size={16} className="mr-1" data-id="element-499" />
                          {getTotalAssignedTrucks(job.id)}/
                          {getRequiredTrucks(job)} Trucks Assigned
                        </span>}
                    </td>
                    <td className="p-3 border-b" data-id="element-500">
                      <div className="flex gap-2" data-id="element-501">
                        <button className="text-blue-600 hover:text-blue-800" onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)} data-id="element-502">
                          {expandedJob === job.id ? 'Hide' : 'Assign'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedJob === job.id && <tr data-id="element-503">
                      <td colSpan={11} className="p-0 border-b" data-id="element-504">
                        <div className="bg-gray-50 p-4" data-id="element-505">
                          <div className="mb-4 flex items-center justify-between" data-id="element-506">
                            <div data-id="element-507">
                              <h3 className="font-medium" data-id="element-508">
                                Trucking Company Assignments
                              </h3>
                              {job.customer && <p className="text-sm text-gray-600 mt-1" data-id="element-509">
                                  Customer:{' '}
                                  <span className="font-medium text-purple-600" data-id="element-510">
                                    {job.customer}
                                  </span>
                                </p>}
                              {job.source === 'contractor' && job.contractorName && <p className="text-sm text-gray-600 mt-1" data-id="element-511">
                                    Job assigned by:{' '}
                                    <span className="font-medium" data-id="element-512">
                                      {job.contractorName}
                                    </span>
                                  </p>}
                            </div>
                            <div className="flex items-center gap-2" data-id="element-513">
                              <span className="text-sm text-gray-600" data-id="element-514">
                                {getTotalAssignedTrucks(job.id)}/
                                {getRequiredTrucks(job)} trucks assigned
                              </span>
                              <button className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm" onClick={() => handleAddTruckingCompany(job.id)} disabled={(assignments[job.id] || []).length >= truckingCompanies.length} data-id="element-515">
                                <PlusCircleIcon size={16} data-id="element-516" />
                                Add Company
                              </button>
                            </div>
                          </div>
                          {(assignments[job.id] || []).map((assignment, index) => <div key={index} className="bg-white p-3 rounded-md mb-2 border" data-id="element-517">
                                <div className="flex items-center gap-4" data-id="element-518">
                                  <div className="flex-grow" data-id="element-519">
                                    <label className="block text-sm font-medium mb-1" data-id="element-520">
                                      Trucking Company
                                    </label>
                                    <select value={assignment.truckingCompanyId} onChange={e => handleChangeCompany(job.id, index, e.target.value)} className="w-full border border-gray-300 rounded-md p-2" data-id="element-521">
                                      {truckingCompanies.map(company => {
                            const isAlreadySelected = assignments[job.id]?.some((a, i) => a.truckingCompanyId === company.id && i !== index);
                            const jobTruckType = job.truckType || '';
                            const rate = company.rates?.[jobTruckType] || company.rates?.['default'] || 0;
                            return <option key={company.id} value={company.id} disabled={isAlreadySelected} data-id="element-522">
                                            {company.name} (
                                            {company.contactPerson}) - $
                                            {rate.toFixed(2)}/hr
                                          </option>;
                          })}
                                    </select>
                                  </div>
                                  <div className="w-48" data-id="element-523">
                                    <label className="block text-sm font-medium mb-1" data-id="element-524">
                                      Number of Trucks
                                    </label>
                                    <input type="number" min="1" max={getRequiredTrucks(job) - getTotalAssignedTrucks(job.id) + assignment.truckCount} value={assignment.truckCount} onChange={e => handleChangeTruckCount(job.id, index, parseInt(e.target.value) || 0)} className="w-full border border-gray-300 rounded-md p-2" data-id="element-525" />
                                  </div>
                                  <div className="flex items-end" data-id="element-526">
                                    <button className="p-2 text-red-500 hover:text-red-700" onClick={() => handleRemoveTruckingCompany(job.id, index)} data-id="element-527">
                                      <MinusCircleIcon size={20} data-id="element-528" />
                                    </button>
                                  </div>
                                </div>
                              </div>)}
                          {isJobFullyAssigned(job) ? <div className="mt-4 flex justify-end" data-id="element-529">
                              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={() => handleAssignJob(job.id)} data-id="element-530">
                                <TruckIcon size={16} data-id="element-531" />
                                Send Job to Trucking Companies
                              </button>
                            </div> : <div className="mt-4 bg-blue-50 p-3 rounded-md flex items-start gap-2" data-id="element-532">
                              <InfoIcon size={18} className="text-blue-500 flex-shrink-0 mt-0.5" data-id="element-533" />
                              <div className="text-sm text-blue-800" data-id="element-534">
                                <p data-id="element-535">
                                  Please assign all {getRequiredTrucks(job)}{' '}
                                  trucks before sending the job.
                                </p>
                                <p className="mt-1" data-id="element-536">
                                  You can split the job among multiple trucking
                                  companies.
                                </p>
                              </div>
                            </div>}
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>}
    </div>;
};